/**
 * Milk sales and To do (16 October 2026), in place of the one tickets page named after a buyer.
 *
 * Ronald, 16 October: "deze pagina blijft chaos en we zouden geen specifieke county pagina meer hebben" (MV-3:
 * no screen specific to one buyer; 8 October: "milk sales of zoiets? en dan county kunnen selecteren?"). So
 * Milk sales holds what is sold, with the buyer as a choice. Since DM-3 (18 October 2026) it is in loads and
 * litres for the transport office; the contracts and prices are on Milk trading (./Trading.tsx).
 * To do holds what waits for a person: the tickets the morning job could not record, and the ones rejected
 * lately.
 *
 * The form is the platform's: a page header, the parts one under the other, and the explanation in the
 * Explainer at the foot instead of a lead paragraph.
 */
import { Badge, Explainer, PageHeader, Section, Select, SelectItem, Table } from "@depicomnlbv/platform-ui";
import { Link, useSearchParams } from "react-router-dom";

import { useOffersPlan, useWeekPlan, type SalesWeekDay, type SalesWeekOrder, type SalesWeekPlan, type SalesWeekTotal, type SalesWeekTotalDay } from "../api";
import { Pending } from "../components/Failure";
import { useMilkFlowAlerts } from "../api/milkFlow";
import { ToDoAlerts } from "../components/MilkFlowAlerts";
import { NewBlocksSection } from "../components/ProductionCalendar";
import { useTitle } from "../useTitle";
import { addDays, formatLitres, formatShortDate, isCalendarDate, mondayOf } from "../time";
import { RecentLoads, ToDoParts } from "./MilkSales";
import { ScreenRefused } from "./salesRefusal";
import "./milkSales.css";

const ALL = "all";

/** What a cell counts: one day of an order, or a total over orders or over the week. */
interface Counts {
  planned: number;
  delivered: number;
  /** Null when not known for every load (a contract without litres per load), and always in a total. */
  litresPlanned: number | null;
  litresDelivered: number;
  withoutLitres: number;
  notes?: string | null;
  /** Loads were planned on a day before today, so nothing delivered means not recorded yet. */
  due: boolean;
}

/** A cell in words, the same for a day, a total and the week, and never in colour alone (WCAG 1.4.1; WP2 spec §2). */
function cellWords(c: Counts): { main: string; more: string[] } {
  const more: string[] = [];
  let main = "";
  if (c.planned > 0) {
    if (c.delivered > 0 || c.due) {
      main = `${c.delivered} of ${c.planned} delivered`;
      if (c.delivered === 0) more.push("not recorded yet");
    } else {
      main = `${c.planned} planned`;
    }
  } else if (c.delivered > 0) {
    main = `${c.delivered} delivered, not planned`;
  }
  if (c.litresPlanned !== null && c.planned > 0) more.push(`${formatLitres(c.litresPlanned)} planned`);
  // A load whose delivery note has no litres yet adds none, so "0 L delivered" would be a figure nobody measured.
  if (c.litresDelivered > 0) more.push(`${formatLitres(c.litresDelivered)} delivered`);
  if (c.withoutLitres > 0) more.push(`${c.withoutLitres} ${c.withoutLitres === 1 ? "load" : "loads"} without litres`);
  if (c.notes) more.push(c.notes);
  return { main, more };
}

function Cell({ c }: { c: Counts }) {
  const { main, more } = cellWords(c);
  if (!main) {
    return (
      <td>
        <span aria-hidden="true" className="nothing">
          —
        </span>
        <span className="visually-hidden">none</span>
      </td>
    );
  }
  return (
    <td>
      {main}
      {more.map((m) => (
        <span className="milk-sales-table__more" key={m}>
          {m}
        </span>
      ))}
    </td>
  );
}

const ofDay = (d: SalesWeekDay, today: string): Counts => ({
  planned: d.loads_planned,
  delivered: d.loads_delivered,
  litresPlanned: d.litres_planned,
  litresDelivered: d.litres_delivered,
  withoutLitres: d.loads_without_litres,
  notes: d.plan_notes,
  due: d.day < today,
});

/** The week of an order: planned litres only when every planned day has them, so the sum is never silently short. */
const ofOrderWeek = (days: SalesWeekDay[], today: string): Counts => {
  const planned = days.filter((d) => d.loads_planned > 0);
  return {
    planned: days.reduce((n, d) => n + d.loads_planned, 0),
    delivered: days.reduce((n, d) => n + d.loads_delivered, 0),
    litresPlanned: planned.length > 0 && planned.every((d) => d.litres_planned !== null)
      ? planned.reduce((n, d) => n + d.litres_planned!, 0)
      : null,
    litresDelivered: days.reduce((n, d) => n + d.litres_delivered, 0),
    withoutLitres: days.reduce((n, d) => n + d.loads_without_litres, 0),
    due: planned.some((d) => d.day < today),
  };
};

const ofTotalDay = (d: SalesWeekTotalDay, today: string): Counts => ({
  planned: d.loads_planned,
  delivered: d.loads_delivered,
  litresPlanned: null,
  litresDelivered: d.litres_delivered,
  withoutLitres: d.loads_without_litres,
  due: d.day < today,
});

const ofTotalWeek = (t: SalesWeekTotal, today: string): Counts => ({
  planned: t.loads_planned,
  delivered: t.loads_delivered,
  litresPlanned: null,
  litresDelivered: t.litres_delivered,
  withoutLitres: t.loads_without_litres,
  due: t.days.some((d) => d.loads_planned > 0 && d.day < today),
});

const DAY_NAME = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric" });
const dayName = (day: string) => DAY_NAME.format(new Date(`${day}T00:00:00Z`));

/** The week plan per order, Monday to Sunday: loads planned against delivered, and the litres delivered. */
function WeekPlanTable({ plan }: { plan: SalesWeekPlan }) {
  const days = plan.totals[0]?.days.map((d) => d.day) ?? [];
  if (plan.orders.length === 0) {
    return <p className="empty">No loads planned or delivered this week.</p>;
  }
  const byBuyer = new Map<number, SalesWeekOrder[]>();
  for (const o of plan.orders) byBuyer.set(o.buyer_id, [...(byBuyer.get(o.buyer_id) ?? []), o]);
  const totalOf = (buyerId: number | null) => plan.totals.find((t) => t.buyer_id === buyerId)!;
  const totalRow = (t: SalesWeekTotal, label: string) => (
    <tr className="milk-sales-table__total" key={`total-${t.buyer_id ?? "all"}`}>
      <th scope="row">{label}</th>
      {t.days.map((d) => (
        <Cell c={ofTotalDay(d, plan.today)} key={d.day} />
      ))}
      <Cell c={ofTotalWeek(t, plan.today)} />
    </tr>
  );
  return (
    <div className="mf-scroll">
      <Table caption={`Loads per order in the week from ${formatShortDate(plan.week)}`} className="milk-sales-table">
        <thead>
          <tr>
            <th scope="col">Order</th>
            {days.map((d) => (
              <th className={d === plan.today ? "is-today" : undefined} key={d} scope="col">
                {dayName(d)}
              </th>
            ))}
            <th scope="col">Week</th>
          </tr>
        </thead>
        <tbody>
          {[...byBuyer.entries()].flatMap(([buyerId, orders]) => [
            ...orders.map((o) => {
              return (
                <tr key={`${buyerId}-${o.sales_contract_id ?? "none"}-${o.sales_offer_id ?? "none"}`}>
                  <th scope="row">
                    {o.buyer}
                    <span className="milk-sales-table__more">
                      {o.contract_number
                        ? `Contract ${o.contract_number}`
                        : o.sales_offer_id
                          ? `Offer ${o.sales_offer_id}, agreed, not yet contracted`
                          : "Without a contract"}
                    </span>
                  </th>
                  {o.days.map((d) => (
                    <Cell c={ofDay(d, plan.today)} key={d.day} />
                  ))}
                  <Cell c={ofOrderWeek(o.days, plan.today)} />
                </tr>
              );
            }),
            totalRow(totalOf(buyerId), `${orders[0]!.buyer}, total`),
          ])}
          {byBuyer.size > 1 ? totalRow(totalOf(null), "All buyers") : null}
        </tbody>
      </Table>
    </div>
  );
}

const STATUS_WORD: Record<string, string> = { proposed: "Proposed", offered: "Offered", agreed: "Agreed" };

/** The offers still in play, in loads and litres: the days transport plans barrels for before a contract. */
function OffersInPlay({ buyer }: { buyer: string | null }) {
  const offers = useOffersPlan();
  if (offers.isPending) return <Pending>Reading the offers</Pending>;
  if (offers.isError) return <ScreenRefused error={offers.error} part="sales" />;
  const rows = offers.data.offers.filter((o) => buyer === null || o.buyer === buyer);
  if (rows.length === 0) {
    return (
      <p className="empty">
        No offer is in play from {formatShortDate(offers.data.from)} to {formatShortDate(offers.data.to)}.
      </p>
    );
  }
  return (
    <Table caption="Offers in play, before a contract" className="milk-sales-table">
      <thead>
        <tr>
          <th scope="col">Buyer</th>
          <th scope="col">Status</th>
          <th scope="col">Loading days</th>
          <th className="dpc-table__num" scope="col">
            Loads
          </th>
          <th className="dpc-table__num" scope="col">
            Litres
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.offer_id}>
            <th scope="row">
              {o.buyer}
              {o.location ? <span className="milk-sales-table__more">at {o.location}</span> : null}
            </th>
            <td>
              <Badge tone={o.status === "agreed" ? "done" : "neutral"}>{STATUS_WORD[o.status] ?? o.status}</Badge>
            </td>
            <td>
              <ul className="milk-sales-table__lines">
                {o.days.map((d) => (
                  <li key={d.loading_date}>
                    {formatShortDate(d.loading_date)}: {d.loads} {d.loads === 1 ? "load" : "loads"}, {formatLitres(d.litres)}
                  </li>
                ))}
              </ul>
            </td>
            <td className="dpc-table__num">{o.loads}</td>
            <td className="dpc-table__num">{formatLitres(o.litres)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/**
 * Milk sales (DM-3, Ronald on the decision sheet, 18 October 2026: "the map stands"): what the transport office
 * needs from sales, in loads and litres and never a price. The week plan per order, the offers in play and the
 * loads sold. The contracts and the offers with their prices are on Milk trading (/trading).
 */
export function SalesPage() {
  useTitle("Milk sales");
  const [search, setSearch] = useSearchParams();
  const asked = search.get("week");
  const monday = isCalendarDate(asked ?? undefined) ? mondayOf(asked!) : null;
  const askedBuyer = search.get("buyer");
  const all = useWeekPlan(monday, null);
  const names = all.data?.buyers.map((b) => b.name) ?? [];
  const buyer = askedBuyer && names.includes(askedBuyer) ? askedBuyer : null;
  const buyerId = all.data?.buyers.find((b) => b.name === buyer)?.buyer_id ?? null;
  const plan = useWeekPlan(monday, buyerId);
  const go = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setSearch(next, { replace: true });
  };
  const week = plan.data?.week ?? monday;
  const link = (w: string) => {
    const next = new URLSearchParams(search);
    next.set("week", w);
    return `/sales?${next.toString()}`;
  };
  return (
    <div className="page page--blocks">
      <PageHeader
        actions={
          names.length > 0 ? (
            <Select
              label="Show sales to"
              onSelectionChange={(key) => go({ buyer: key === ALL ? null : String(key) })}
              selectedKey={buyer ?? ALL}
            >
              <SelectItem id={ALL}>All buyers</SelectItem>
              {names.map((n) => (
                <SelectItem id={n} key={n}>
                  {n}
                </SelectItem>
              ))}
            </Select>
          ) : null
        }
        period={
          week
            ? {
                label: "Week",
                current: `Week from ${formatShortDate(week)}`,
                previous: <Link to={link(addDays(week, -7))}>Previous week</Link>,
                next: <Link to={link(addDays(week, 7))}>Next week</Link>,
              }
            : undefined
        }
        title="Milk sales"
      />
      <Section title="Week plan">
        {plan.isPending ? (
          <Pending>Reading the week plan</Pending>
        ) : plan.isError ? (
          <ScreenRefused error={plan.error} part="sales" />
        ) : (
          <WeekPlanTable plan={plan.data} />
        )}
      </Section>
      <Section title="Offers in play">
        <OffersInPlay buyer={buyer} />
      </Section>
      <Section title="Loads sold">
        <RecentLoads buyer={buyer} />
      </Section>
      <Explainer summary="How milk sales work">
        <dl className="explainer-list">
          <dt>Week plan</dt>
          <dd>
            Per order and day, the loads planned against the loads delivered, and the litres delivered. A load sold
            without a contract has its own row under its buyer, so the buyer&rsquo;s total adds up to what was
            delivered. Planned litres are not added up, because a contract without litres per load has none.
          </dd>
          <dt>Offers in play</dt>
          <dd>
            What we offer to sell before the buyer&rsquo;s confirmation: the loading days to plan barrels for. Once the
            buyer&rsquo;s confirmation is linked, the offer is an order in the week plan.
          </dd>
          <dt>Loads sold</dt>
          <dd>
            From the haulier&rsquo;s Optiload tickets. Collected is the farms&rsquo; whole collections that day; sold is what was
            transferred into the buyer&rsquo;s tanker.
          </dd>
          <dt>Prices</dt>
          <dd>
            No price is shown here. The contracts and the offers with their prices are on Milk trading, for milk
            trading, the milk office and management.
          </dd>
        </dl>
      </Explainer>
    </div>
  );
}

export function ToDoPage() {
  useTitle("To do");
  // The risks of the next 48 hours (AP-4); a caller who may not read Milk Flow simply sees none.
  const alerts = useMilkFlowAlerts();
  return (
    <div className="page page--blocks">
      <PageHeader title="To do" />
      {alerts.data ? <ToDoAlerts alerts={alerts.data.alerts ?? []} today={alerts.data.today} /> : null}
      <NewBlocksSection />
      <ToDoParts />
      <Explainer summary="What waits here">
        <dl className="explainer-list">
          <dt>Waiting for the milk office</dt>
          <dd>
            A ticket the morning job could not record by itself, with the reasons and a link to the mail with the scan.
            Correct the farm, the litres and the time of each line, and confirm or reject it. Nothing is guessed: what
            the job proposed is shown as proposed, and only a person&rsquo;s confirmation records it.
          </dd>
          <dt>Overdue</dt>
          <dd>
            What is still open from a day that has passed, such as delivery notes not in by noon the day after
            loading, with the people who hold the role that acts on it.
          </dd>
          <dt>Within 48 hours</dt>
          <dd>
            What the milk does not cover in the next 48 hours: a batch, barrels short or a late tanker, with the people
            who hold the role that acts on it. Milk Flow and Plan show how many are open, with a link here.
          </dd>
          <dt>New on the production calendar</dt>
          <dd>
            A block entered in the last seven days whose days are still to come, with who entered it and the people it
            concerns: the dispatcher, trading and the cheesemakers. It is in force as soon as it is entered. Anyone can
            object; an objection is shown beside the block and stops nothing. A block the system proposes waits here
            until someone in production planning accepts or rejects it.
          </dd>
          <dt>Rejected lately</dt>
          <dd>A rejection says why, since it leaves a farmer unpaid for that day until it is resolved.</dd>
        </dl>
      </Explainer>
    </div>
  );
}
